import React from 'react'
import { Link } from '@chakra-ui/react'
import {
    formatDistanceToNowStrict,
    format,
    isToday,
    isYesterday,
    isThisWeek,
    isThisYear,
} from 'date-fns'
import {
    ChatMessageFieldsFragment,
    MessageMethod,
    MessageType,
} from '~graphql/generated/graphql'

/**
 * True when a message went out over app push only and the push failed —
 * typically because the client has no Chckvet app account or device token.
 * Nothing was delivered, so the message must surface as not sent.
 *
 * Push failures on multi-channel sends are intentionally ignored: push is
 * often sent alongside SMS/email and most clients don't have the app, so
 * those incidental errors would flag successfully-delivered messages.
 */
export function isFailedPushOnlySend(
    message: Pick<
        ChatMessageFieldsFragment,
        'message_methods' | 'push_error'
    >,
) {
    const methods = message.message_methods ?? []
    return (
        methods.length === 1 &&
        methods[0] === MessageMethod.Push &&
        !!message.push_error
    )
}

export function checkIfLastMessageWasSame(
    last: ChatMessageFieldsFragment,
    current: ChatMessageFieldsFragment,
) {
    if (
        last.client &&
        current.client &&
        last.client?.id === current.client?.id
    ) {
        return true
    }

    if (
        last.clinic_user &&
        current.clinic_user &&
        last.clinic_user?.id === last.clinic_user?.id
    ) {
        return true
    }

    if (
        last.message_type === MessageType.System &&
        current.message_type === MessageType.System
    ) {
        return true
    }

    return false
}

export function formatHumanReadableDate(timestamp: number): string {
    const messageDate = new Date(timestamp)

    // Check if message is from today
    if (isToday(messageDate)) {
        return ` ${formatDistanceToNowStrict(messageDate)} ago at ${format(
            messageDate,
            'h:mm a',
        )}`
    }

    // Check if message is from yesterday
    if (isYesterday(messageDate)) {
        return `Yesterday at ${format(messageDate, 'h:mm a')}`
    }

    // Check if message is from this week (but not today or yesterday)
    if (isThisWeek(messageDate, { weekStartsOn: 1 })) {
        // week starts on Monday
        return `${format(messageDate, 'EEEE')} at ${format(messageDate, 'h:mm a')}`
    }

    // Check if message is from this year (but not this week)
    if (isThisYear(messageDate)) {
        return `${format(messageDate, 'EEE, MMM dd')} at ${format(messageDate, 'h:mm a')}`
    }

    // Anything beyond this year
    return `${format(messageDate, 'EEE, MMM dd yy')} at ${format(messageDate, 'h:mm a')}`
}

export function parseMessageText({ text }: { text: string }) {
    const nodes = tokenizeMessageText(text).map((token, index) => {
        if (token.kind === 'url') {
            return (
                <Link
                    key={`url-${index}`}
                    href={token.url}
                    color="inherit"
                    textDecoration="underline"
                    isExternal
                >
                    {token.url}
                </Link>
            )
        }

        return (
            <React.Fragment key={`text-${index}`}>{token.text}</React.Fragment>
        )
    })

    return <>{nodes}</>
}

type MessageTextToken =
    | {
          kind: 'text'
          text: string
      }
    | {
          kind: 'url'
          url: string
      }

const URL_REGEX = /https?:\/\/[^\s]+/g
const TRAILING_URL_PUNCTUATION_REGEX = /[),.!?;:]+$/

function tokenizeMessageText(text: string): MessageTextToken[] {
    const tokens: MessageTextToken[] = []
    let lastIndex = 0

    for (const match of text.matchAll(URL_REGEX)) {
        const matchIndex = match.index ?? 0
        const rawUrl = match[0]
        const url = rawUrl.replace(TRAILING_URL_PUNCTUATION_REGEX, '')
        const trailingText = rawUrl.slice(url.length)

        if (matchIndex > lastIndex) {
            tokens.push({
                kind: 'text',
                text: text.slice(lastIndex, matchIndex),
            })
        }

        tokens.push({
            kind: 'url',
            url,
        })

        if (trailingText) {
            tokens.push({
                kind: 'text',
                text: trailingText,
            })
        }

        lastIndex = matchIndex + rawUrl.length
    }

    if (lastIndex < text.length) {
        tokens.push({
            kind: 'text',
            text: text.slice(lastIndex),
        })
    }

    return tokens
}
